import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sparkles, HelpCircle } from 'lucide-react';
import FormBuilderHeader from '../components/common/formbuilder/FormBuilderHeader';
import Sidebar from '../components/common/formbuilder/Sidebar';
import FormCanvas from '../components/common/formbuilder/FormCanvas';
import SettingsPanel from '../components/common/formbuilder/SettingsPanel';
import QuestionTypeSelector from '../components/common/formbuilder/QuestionTypeSelector';
import PreviewModal from '../components/common/formbuilder/PreviewModal';
import PublishSuccessModal from '../components/common/formbuilder/PublishSuccessModal';
import PublishingModal from '../components/common/formbuilder/PublishingModal';
import FormBuilderTour from '../components/common/formbuilder/FormBuilderTour';
import useFormBuilder from '../hooks/useFormBuilder';
import { validateForm } from '../utils/formValidation';
import PageLoader from '../components/common/PageLoader';

const FormBuilder = () => {
  const { formId } = useParams();
  const navigate = useNavigate();

  const {
    form,
    quests,
    selectedQuestion,
    selectedQuestionId,
    isSaving,
    isPublishing,
    lastSaved,
    isLoading,
    hasUnsavedChanges,
    updateForm,
    updateFormSetting,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    duplicateQuestion,
    reorderQuestions,
    deleteAllQuestions,
    setSelectedQuestionId,
    createNewForm,
    publishForm,
    importQuestions,
    undo,
    redo,
    canUndo,
    canRedo
  } = useFormBuilder(formId);

  const [showQuestionTypeSelector, setShowQuestionTypeSelector] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [activeSection, setActiveSection] = useState('settings');
  const [showMobileSettings, setShowMobileSettings] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [runTour, setRunTour] = useState(false);

  // Check if user is a first-time visitor and start tour
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('fylo_formbuilder_tour_completed');
    if (!hasSeenTour && !formId) {
      // Only show tour for new forms, not when editing existing forms
      // Add delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        setRunTour(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formId]);

  const handleTourComplete = () => {
    localStorage.setItem('fylo_formbuilder_tour_completed', 'true');
    setRunTour(false);
  };

  const handleStartTour = () => {
    setRunTour(true);
  };

  // Check form limit on mount for new forms
  useEffect(() => {
    const checkFormLimit = async () => {
      // Only check limit for new forms (no formId)
      if (!formId && !isLoading) {
        try {
          const API_URL = process.env.REACT_APP_API_URL;

          // Get user info to check plan
          const userResponse = await fetch(`${API_URL}/users/me`, {
            credentials: 'include'
          });

          if (userResponse.ok) {
            const userData = await userResponse.json();
            const currentPlan = userData.data?.subscription_plan || 'free';

            if (currentPlan === 'free') {
              // Get user's forms to check count
              const formsResponse = await fetch(`${API_URL}/forms`, {
                credentials: 'include'
              });

              if (formsResponse.ok) {
                const formsData = await formsResponse.json();
                const formCount = formsData.data?.length || 0;

                if (formCount >= 5) {
                  // Redirect back with error message
                  navigate('/dashboard/forms', { replace: true });
                  toast.error('Free plan limit reached. Upgrade to create more forms.');
                }
              }
            }
          }
        } catch (error) {
          console.error('Error checking form limit:', error);
        }
      }
    };

    checkFormLimit();
  }, [formId, isLoading, navigate]);

  const handleTitleChange = (e) => {
    updateForm('title', e.target.value);
  };

  const handleBack = () => {
    window.history.back();
  };

  const handleAddQuestion = () => {
    setShowQuestionTypeSelector(true);
  };

  const handleSelectQuestionType = (type) => {
    addQuestion(type);
    setShowQuestionTypeSelector(false);
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handlePublish = async () => {
    // Prevent multiple clicks - early return if already publishing
    if (isPublishing) {
      return;
    }

    const validation = validateForm(form);

    if (!validation.isValid) {
      alert('Please fix the following errors:\n\n' + validation.errors.join('\n'));
      return;
    }

    try {
      let formIdToPublish = form.id;

      // If form doesn't have an ID yet, create it first
      if (!formIdToPublish) {
        const createdForm = await createNewForm();
        if (createdForm) {
          formIdToPublish = createdForm.id;
          // Update URL to include form ID
          navigate(`/form-builder/${createdForm.id}`, { replace: true });
        } else {
          // If creation failed, stop here
          return;
        }
      }

      // Then publish using the form ID (either existing or newly created)
      const publishedForm = await publishForm(formIdToPublish);
      if (publishedForm) {
        setShowPublishSuccess(true);
      }
    } catch (error) {
      console.error('Publish error:', error);
    }
  };

  const handleQuestionSettingChange = (setting, value) => {
    if (selectedQuestion) {
      updateQuestion(selectedQuestion.id, { [setting]: value });
    }
  };

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      // Prevent undo/redo when typing in input fields
      const isInputField = ['INPUT', 'TEXTAREA'].includes(e.target.tagName);
      if (isInputField) return;

      // Ctrl+Z or Cmd+Z: Undo
      if (isCtrlOrCmd && e.key === 'z' && !e.shiftKey && canUndo) {
        e.preventDefault();
        undo();
      }

      // Ctrl+Y or Ctrl+Shift+Z or Cmd+Shift+Z: Redo
      if (isCtrlOrCmd && ((e.key === 'y' && !e.shiftKey) || (e.key === 'z' && e.shiftKey)) && canRedo) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, canUndo, canRedo]);

  // Show loading state while fetching form
  if (isLoading) {
    return <PageLoader text="Loading form..." variant="pulse" />;
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <FormBuilderHeader
        formTitle={form.title}
        onTitleChange={handleTitleChange}
        isSaving={isSaving}
        isPublishing={isPublishing}
        lastSaved={lastSaved}
        onPreview={handlePreview}
        onPublish={handlePublish}
        onBack={handleBack}
        formData={form}
        hasUnsavedChanges={hasUnsavedChanges}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onDeleteAll={deleteAllQuestions}
        questionCount={form.questions.length}
      />

      {/* Main Content - Responsive Layout */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Left Sidebar - Desktop */}
        <Sidebar
          onAddQuestion={handleAddQuestion}
          onNavigate={setActiveSection}
          activeSection={activeSection}
          isMobile={false}
          isOpen={showMobileSidebar}
          onClose={() => setShowMobileSidebar(false)}
        />

        {/* Center Canvas */}
        <FormCanvas
          formTitle={form.title}
          formDescription={form.description}
          questions={form.questions}
          selectedQuestionId={selectedQuestionId}
          formSettings={form.settings}
          onFormUpdate={updateForm}
          onQuestionSelect={setSelectedQuestionId}
          onQuestionUpdate={updateQuestion}
          onQuestionDelete={deleteQuestion}
          onQuestionDuplicate={duplicateQuestion}
          onAddQuestion={handleAddQuestion}
          onReorderQuestions={reorderQuestions}
        />

        {/* Right Settings Panel - Desktop & Mobile Drawer */}
        <SettingsPanel
          selectedQuestion={selectedQuestion}
          formSettings={form.settings}
          onFormSettingChange={updateFormSetting}
          onQuestionSettingChange={handleQuestionSettingChange}
          onAIImport={importQuestions}
          isMobile={showMobileSettings}
          onClose={() => setShowMobileSettings(false)}
        />

        {/* Mobile Floating Action Button - Add Question */}
        <button
          onClick={handleAddQuestion}
          className="lg:hidden fixed bottom-20 sm:bottom-24 right-3 sm:right-4 w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 text-white rounded-full border-2 border-blue-700 hover:bg-blue-700 hover:border-blue-800 transition-all duration-300 flex items-center justify-center z-40 shadow-lg active:scale-95"
          aria-label="Add Question"
        >
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>

        {/* Mobile AI Import/Form Settings Button - Shows when NO question is selected */}
        {!selectedQuestion && (
          <button
            onClick={() => setShowMobileSettings(true)}
            className="xl:hidden fixed bottom-20 sm:bottom-24 left-3 sm:left-4 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full border-2 border-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center z-40 shadow-lg active:scale-95"
            aria-label="AI Import & Form Settings"
          >
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        )}

        {/* Mobile Question Settings Button - Shows when question IS selected */}
        {selectedQuestion && (
          <button
            onClick={() => setShowMobileSettings(true)}
            className="xl:hidden fixed bottom-20 sm:bottom-24 left-3 sm:left-4 w-12 h-12 sm:w-14 sm:h-14 bg-gray-800 text-white rounded-full border-2 border-gray-900 hover:bg-gray-900 transition-all duration-300 flex items-center justify-center z-40 shadow-lg active:scale-95"
            aria-label="Question Settings"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        )}

        {/* Help Button - Start Tour */}
        <button
          onClick={handleStartTour}
          className="fixed bottom-3 sm:bottom-4 right-3 sm:right-4 w-10 h-10 sm:w-12 sm:h-12 bg-white text-blue-600 rounded-full border-2 border-blue-600 hover:bg-blue-50 transition-all duration-300 flex items-center justify-center z-40 shadow-lg active:scale-95"
          title="Start Tour"
          aria-label="Start Tour"
        >
          <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      {/* Modals */}
      <QuestionTypeSelector
        isOpen={showQuestionTypeSelector}
        onClose={() => setShowQuestionTypeSelector(false)}
        onSelectType={handleSelectQuestionType}
      />

      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        form={form}
        quests={quests}
      />

      <PublishingModal isOpen={isPublishing} />

      <PublishSuccessModal
        isOpen={showPublishSuccess}
        onClose={() => setShowPublishSuccess(false)}
        formId={form.id}
      />

      {/* Product Tour */}
      <FormBuilderTour run={runTour} onComplete={handleTourComplete} />
    </div>
  );
};

export default FormBuilder;